import { useCallback, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import Button from "../../../atoms/Button";
import Heading from "../../../atoms/Heading";
import InputField from "../../../atoms/InputField";
import RentaSection from "../../../molecules/inmueblesCreate/RentaSection";
import { initialValuesInmueble } from "../../../molecules/inmueblesCreate/formSchemaInmueble";
import { validationSchemaRent } from "./formSchemaRent";
import { REGISTER_USER } from "../../../../api/apiUsers";
import { UPDATE_INMUEBLE } from "../../../../api/apiInmuebles";
import { GET_RENTA_ARCHIVOS, REGISTER_RENTA, UPDATE_RENTA_ARCHIVOS } from "../../../../api/apiFiles";
import { REGISTER_EDO_CTA } from "../../../../api/apiEdoCuenta";
import { getMonthsBetweenDates } from "../../../../utils/dateUtils";

const RentEdit = ({ inmueble, refetch, onClose }) => {
    const [renta, setRenta] = useState();
    const [generatedCode, setGeneratedCode] = useState("");
    const [archivosRenta, setArchivosRenta] = useState({
        contrato_renta: null,
		ine_inquilino: null,
		datos_facturacion: null,
		poliza_aval: null,
    });

    useEffect(() => {
        if (inmueble.isRented) {
            const newCode = Math.random().toString(36).substring(2, 8).toUpperCase();
            setGeneratedCode(newCode);
        }
    }, [inmueble.isRented]);

    const getRenta = useCallback( async () => {
        try {
            const rentaData = await GET_RENTA_ARCHIVOS(inmueble.id);
            const rentaActiva = rentaData?.find(renta => renta.isActive === true);

            if (!rentaActiva) {
                toast.error("No hay una renta activa para este inmueble");
                onClose();
                return;
            }
            setRenta(rentaActiva);
        } catch (error) {
            toast.error("Error al obtener la información");
            onClose();
        };
    }, [inmueble, onClose]);

    useEffect(() => {
        if (inmueble?.isRented) {
            getRenta();
        }
    }, [getRenta, inmueble]);

    const handleRegisterEdoCtaMonthly = async ({ propertieId, userId, total, iva, subtotal, date_start, date_end }) => {
        const months = getMonthsBetweenDates(date_start, date_end);

        for (const { from, to } of months) {
            const monthLabel = `${from} - ${to}`;

            const newEdoCuenta = {
                propertieId,
                userId,
                total,
                iva,
                subtotal,
                month: monthLabel,
                status: false,
            };

            try {
                await REGISTER_EDO_CTA(newEdoCuenta);
            } catch (error) {
                console.error("Error registrando estado de cuenta mensual:", error);
            }
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            setSubmitting(true);

            let idClient = null;
            if (values.isRented === true && !values.userId) {
                try {
                    const response = await REGISTER_USER({ email:values.userEmail, name:values.userName, role:"Resident"});
                    idClient = response.user.user.id;
                } catch (error) {
                    console.error(error);
                }
            };

            if (values.isRented) {
                const datos_facturacion = {
                    razonSocial: values.fac_razonSocial,
                    rfc: values.fac_rfc,
                    usoCFDI: values.fac_usoCFDI,
                    email: values.fac_email
                };

                const payloadRenta = {
                    ...values,
                    datos_facturacion: JSON.stringify(datos_facturacion),
                    userId: values.userId ?? idClient
                };

                await REGISTER_RENTA(inmueble.id, archivosRenta, payloadRenta);
                await UPDATE_INMUEBLE(inmueble.id, {
                    isRented: true,
                    userId: values.userId ?? idClient
                });
                await handleRegisterEdoCtaMonthly({
                    propertieId: inmueble.id,
                    userId: values.userId || idClient,
                    total: values.monto,
                    iva: values.iva,
                    subtotal: values.subtotal,
                    date_start: values.date_start,
                    date_end: values.date_end
                });
            } else if (!values.isRented) {
                if (values.code === generatedCode) {
                    await UPDATE_RENTA_ARCHIVOS(renta?.id, {}, {isActive: false});
                    await UPDATE_INMUEBLE(inmueble.id, { isRented: false, userId: null});
                } else {
                    toast.error("El código ingresado no es válido.");
                    return;
                }
            }
            refetch();
            onClose();
            toast.success("Inmueble actualizado correctament");
        } catch (error) {
            toast.error("Error al actualizar el inmueble")
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <Heading
                text={inmueble.isRented ? "Confirme para eliminar la renta" : "Active y complete los campos para rentar" }
            />
            <Formik
                onSubmit={handleSubmit}
                validationSchema={validationSchemaRent}
                initialValues={{ ...initialValuesInmueble, code:"" }}
            >
                {({ values, isSubmitting, setFieldValue }) => (
                    <Form className="flex flex-col justify-center items-center gap-5">
                        {!inmueble.isRented &&
                            <RentaSection values={values} setFieldValue={setFieldValue} setArchivosRenta={setArchivosRenta} />
                        }
                        {inmueble.isRented && !values.isRented &&
                            <div className="w-full flex flex-col space-y-4 justify-center items-center">
                                <div className="w-1/2 bg-gray-300 rounded-lg p-4 select-none text-center"
                                    onContextMenu={(e) => e.preventDefault()}
                                    onCopy={(e) => e.preventDefault()}
                                    onCut={(e) => e.preventDefault()}
                                    onPaste={(e) => e.preventDefault()}
                                    >
                                    <p className="font-bold tracking-widest text-lg">{generatedCode}</p>
                                </div>
                                <InputField name="code" label="Código de confirmación" placeholder="Ingresa el código" value={values.code} />
                            </div>
                        }
                        <Button text={isSubmitting ? <PropagateLoader color="red" /> : "Agregar"} type="submit" bgColor="blue" disabled={isSubmitting}/>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default RentEdit;